import { toast } from "sonner";
import { call, put, takeLatest, takeEvery } from "redux-saga/effects";
import {
  assignTransactionToInvoice as assignTransactionToInvoiceService,
  createInvoice as createInvoiceService,
  deleteInvoice as deleteInvoiceService,
  getFinancialTransactions as getFinancialTransactionsService,
  getInvoiceById as getInvoiceByIdService,
  getInvoicesByPatient as getInvoicesByPatientService,
  initiatePayment as initiatePaymentService,
  markInvoiceAsPaidCash as markInvoiceAsPaidCashService
} from "../../services/invoices";
import * as types from "./types";
import { adaptResponseError } from "../../utils/utils";

/**
 * Sagas du domaine Invoice + FinancialTransaction.
 *
 * <p>Conventions :
 * <ul>
 *   <li>Tous les appels HTTP passent par {@code call(service, ...)}.</li>
 *   <li>Les services renvoient l'enveloppe apiRequest
 *       {@code {ok, status, data, message}}.</li>
 *   <li>POST /invoice ne retourne qu'un Long (id) — on chaîne
 *       getInvoiceByIdService pour récupérer le DTO complet.</li>
 *   <li>On rafraîchit la liste du patient si {@code payload.patientId}
 *       est présent dans le payload de création.</li>
 * </ul>
 */

/* ------------------------------------------------------------------ */
/* Invoice                                                             */
/* ------------------------------------------------------------------ */

function* createInvoice({ payload }) {
  try {
    const result = yield call(createInvoiceService, payload);
    if (!result?.ok) {
      yield put({
        type: types.CREATE_INVOICE_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }

    const newId = result.data;
    let createdInvoice = { id: newId };

    if (newId != null) {
      const detail = yield call(getInvoiceByIdService, newId);
      if (detail?.ok) {
        createdInvoice = detail.data;
      }
    }

    toast.success("Facture créée avec succès");
    yield put({
      type: types.CREATE_INVOICE_SUCCESS,
      payload: createdInvoice
    });

    const patientId = payload?.patientId;
    if (patientId != null) {
      yield put({
        type: types.GET_INVOICES_BY_PATIENT_REQUEST,
        payload: { patientId }
      });
    }
  } catch (error) {
    console.error("CreateInvoiceSagaErr: ", error);
    toast.error("Erreur inattendue lors de la création de la facture");
    yield put({
      type: types.CREATE_INVOICE_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

function* deleteInvoice({ payload }) {
  try {
    const id = payload?.id;
    if (id == null) {
      yield put({
        type: types.DELETE_INVOICE_FAILURE,
        payload: [{ message: "id manquant" }]
      });
      return;
    }
    const result = yield call(deleteInvoiceService, id);
    if (!result?.ok) {
      yield put({
        type: types.DELETE_INVOICE_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    toast.success("Facture supprimée");
    yield put({ type: types.DELETE_INVOICE_SUCCESS, payload: { id } });
  } catch (error) {
    console.error("DeleteInvoiceSagaErr: ", error);
    yield put({
      type: types.DELETE_INVOICE_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

function* getInvoiceById({ payload }) {
  try {
    const id = payload?.id;
    if (id == null) {
      yield put({
        type: types.GET_INVOICE_BY_ID_FAILURE,
        payload: [{ message: "id manquant" }]
      });
      return;
    }
    const result = yield call(getInvoiceByIdService, id);
    if (!result?.ok) {
      yield put({
        type: types.GET_INVOICE_BY_ID_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    yield put({
      type: types.GET_INVOICE_BY_ID_SUCCESS,
      payload: result.data
    });
  } catch (error) {
    console.error("GetInvoiceByIdSagaErr: ", error);
    yield put({
      type: types.GET_INVOICE_BY_ID_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

function* getInvoicesByPatient({ payload }) {
  try {
    const { patientId } = payload || {};
    if (patientId == null) {
      yield put({
        type: types.GET_INVOICES_BY_PATIENT_FAILURE,
        payload: [{ message: "patientId manquant" }]
      });
      return;
    }
    const result = yield call(getInvoicesByPatientService, patientId);
    if (!result?.ok) {
      yield put({
        type: types.GET_INVOICES_BY_PATIENT_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    yield put({
      type: types.GET_INVOICES_BY_PATIENT_SUCCESS,
      payload: Array.isArray(result.data) ? result.data : []
    });
  } catch (error) {
    console.error("GetInvoicesByPatientSagaErr: ", error);
    yield put({
      type: types.GET_INVOICES_BY_PATIENT_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

function* initiatePayment({ payload }) {
  try {
    const { invoiceId } = payload || {};
    if (invoiceId == null) {
      yield put({
        type: types.INITIATE_PAYMENT_FAILURE,
        payload: [{ message: "invoiceId manquant" }]
      });
      return;
    }
    const result = yield call(initiatePaymentService, invoiceId);
    if (!result?.ok) {
      yield put({
        type: types.INITIATE_PAYMENT_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    yield put({
      type: types.INITIATE_PAYMENT_SUCCESS,
      payload: result.data
    });
  } catch (error) {
    console.error("InitiatePaymentSagaErr: ", error);
    yield put({
      type: types.INITIATE_PAYMENT_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

function* markInvoiceAsPaidCash({ payload }) {
  try {
    const { invoiceId } = payload || {};
    if (invoiceId == null) {
      yield put({
        type: types.MARK_INVOICE_AS_PAID_CASH_FAILURE,
        payload: [{ message: "invoiceId manquant" }]
      });
      return;
    }
    const result = yield call(markInvoiceAsPaidCashService, invoiceId);
    if (!result?.ok) {
      yield put({
        type: types.MARK_INVOICE_AS_PAID_CASH_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    toast.success("Facture marquée comme payée en espèces");
    yield put({
      type: types.MARK_INVOICE_AS_PAID_CASH_SUCCESS,
      payload: { invoiceId }
    });
    yield put({
      type: types.GET_INVOICE_BY_ID_REQUEST,
      payload: { id: invoiceId }
    });
  } catch (error) {
    console.error("MarkInvoiceAsPaidCashSagaErr: ", error);
    yield put({
      type: types.MARK_INVOICE_AS_PAID_CASH_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

/* ------------------------------------------------------------------ */
/* FinancialTransaction                                                */
/* ------------------------------------------------------------------ */

function* getFinancialTransactions() {
  try {
    const result = yield call(getFinancialTransactionsService);
    if (!result?.ok) {
      yield put({
        type: types.GET_FINANCIAL_TRANSACTIONS_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    yield put({
      type: types.GET_FINANCIAL_TRANSACTIONS_SUCCESS,
      payload: result.data
    });
  } catch (error) {
    console.error("GetFinancialTransactionsSagaErr: ", error);
    yield put({
      type: types.GET_FINANCIAL_TRANSACTIONS_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

function* assignTransactionToInvoice({ payload }) {
  try {
    const { invoiceId, transactionKey } = payload || {};
    if (invoiceId == null || !transactionKey) {
      yield put({
        type: types.ASSIGN_TRANSACTION_TO_INVOICE_FAILURE,
        payload: [{ message: "invoiceId ou transactionKey manquant" }]
      });
      return;
    }
    const result = yield call(
      assignTransactionToInvoiceService,
      invoiceId,
      transactionKey
    );
    if (!result?.ok) {
      yield put({
        type: types.ASSIGN_TRANSACTION_TO_INVOICE_FAILURE,
        payload: adaptResponseError(result || {})
      });
      return;
    }
    toast.success("Transaction assignée à la facture");
    yield put({
      type: types.ASSIGN_TRANSACTION_TO_INVOICE_SUCCESS,
      payload: { invoiceId, transactionKey }
    });
    yield put({ type: types.GET_FINANCIAL_TRANSACTIONS_REQUEST });
  } catch (error) {
    console.error("AssignTransactionToInvoiceSagaErr: ", error);
    yield put({
      type: types.ASSIGN_TRANSACTION_TO_INVOICE_FAILURE,
      payload: [{ message: error?.message || "Erreur inattendue" }]
    });
  }
}

export default function* InvoicesSaga() {
  yield takeLatest(types.CREATE_INVOICE_REQUEST, createInvoice);
  yield takeLatest(types.DELETE_INVOICE_REQUEST, deleteInvoice);
  yield takeEvery(types.GET_INVOICE_BY_ID_REQUEST, getInvoiceById);
  yield takeLatest(
    types.GET_INVOICES_BY_PATIENT_REQUEST,
    getInvoicesByPatient
  );
  yield takeLatest(types.INITIATE_PAYMENT_REQUEST, initiatePayment);
  yield takeLatest(types.MARK_INVOICE_AS_PAID_CASH_REQUEST, markInvoiceAsPaidCash);
  yield takeLatest(
    types.GET_FINANCIAL_TRANSACTIONS_REQUEST,
    getFinancialTransactions
  );
  yield takeLatest(
    types.ASSIGN_TRANSACTION_TO_INVOICE_REQUEST,
    assignTransactionToInvoice
  );
}
